import React, { useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { signInWithGoogle, signInWithFacebook, firebaseSignOut } from '../lib/firebase';

// "Continue with Google" + "Continue with Facebook" buttons.
// On success, posts the Firebase ID token to our backend /api/auth/firebase
// which mints the app JWT and either finds or creates the matching user row.
//
// Social logins are auto-verified — no email-verification modal.

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"/>
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A9 9 0 0 0 9 18z"/>
    <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3.01-2.32z"/>
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.34l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .96 4.96l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#1877F2" d="M24 12C24 5.37 18.63 0 12 0S0 5.37 0 12c0 5.99 4.39 10.95 10.13 11.85V15.47H7.08V12h3.05V9.36c0-3.01 1.79-4.67 4.53-4.67 1.31 0 2.69.23 2.69.23v2.95h-1.51c-1.49 0-1.96.93-1.96 1.88V12h3.34l-.53 3.47h-2.81v8.38C19.61 22.95 24 17.99 24 12z"/>
  </svg>
);

const SocialButton = ({ onClick, loading, disabled, icon, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading || disabled}
    data-testid={testid}
    className="w-full flex items-center justify-center gap-3 rounded-full py-3 px-6
               bg-card border border-primary/30 hover:border-primary/60
               text-foreground hover:bg-primary/5 transition-colors
               disabled:opacity-50 disabled:cursor-not-allowed
               font-medium tracking-wide"
  >
    {icon}
    <span>{loading ? 'Please wait…' : label}</span>
  </button>
);

const SocialLoginButtons = ({ onSuccess, disabled }) => {
  const { loginWithFirebase } = useAuth();
  const [busy, setBusy] = useState(''); // 'google' | 'facebook' | ''

  const handle = async (provider, signInFn) => {
    setBusy(provider);
    try {
      const user = await signInFn();
      const idToken = await user.getIdToken(true);
      const appUser = await loginWithFirebase(idToken, user.displayName || '');
      toast.success(`Welcome${appUser.name ? `, ${appUser.name.split(' ')[0]}` : ''}!`);
      if (onSuccess) onSuccess(appUser);
    } catch (err) {
      // User-cancelled popup is normal — don't toast.
      const code = err?.code || '';
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        // silent
      } else if (code === 'auth/account-exists-with-different-credential') {
        toast.error('This email is registered with another provider. Use the original method to sign in.');
      } else {
        const msg = err?.response?.data?.detail || err?.message || 'Sign-in failed';
        toast.error(typeof msg === 'string' ? msg : 'Sign-in failed');
      }
      // Clean up the Firebase session if we failed before the backend handshake completed.
      await firebaseSignOut();
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="space-y-3" data-testid="social-login-buttons">
      <SocialButton
        onClick={() => handle('google', signInWithGoogle)}
        loading={busy === 'google'}
        disabled={disabled || busy === 'facebook'}
        icon={<GoogleIcon />}
        label="Continue with Google"
        testid="social-login-google"
      />
      <SocialButton
        onClick={() => handle('facebook', signInWithFacebook)}
        loading={busy === 'facebook'}
        disabled={disabled || busy === 'google'}
        icon={<FacebookIcon />}
        label="Continue with Facebook"
        testid="social-login-facebook"
      />
    </div>
  );
};

export default SocialLoginButtons;
